import { Tab, Tabs } from '@mui/material';
import { FC, useState } from 'react';

import checkIcon from 'assets/images/checkIcon.svg';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentTabbedSwitcher } from 'graphqlTypes';

const TabbedSwitcherV2: FC<ContentfulComponentTabbedSwitcher> = ({ switcherItem, ...props }) => {
  const [active, setActive] = useState(0);

  const sx = {
    fontWeight: '700',
    color: '#C1C2C3',
    minWidth: 'max-content',
    textTransform: 'capitalize',

    '&&.Mui-selected': {
      color: '#000',
    },
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActive(newValue);
  };

  const longestListItemLength = switcherItem.reduce((maxLength, item) => Math.max(maxLength, item.listItem.length), 0);

  return (
    <Flex
      width="100%"
      flexDirection="row"
      borderRadius="12px"
      border="1px solid"
      borderColor="gray-200"
      overflowX="hidden"
      overflowY="hidden"
      height={`${580 + 50 * longestListItemLength}px`}
      sm={{ height: `${670 + 50 * longestListItemLength}px` }}
      md={{ height: `${500 + 50 * longestListItemLength}px` }}
      lg={{ height: `${450 + 50 * longestListItemLength}px` }}
      css={`
        @media (max-width: 767px) {
          flex-direction: column;
        }
      `}
      {...props}
    >
      <Flex width="100%" height="250px" md={{ width: '50%', height: '100%' }}>
        {switcherItem?.map((item, index) => (
          <img
            src={item?.reference?.imageDesktop?.file?.url}
            className={`w-full object-cover h-[250px] sm:h-full ${active === index ? 'visible' : 'hidden'}`}
            key={`featured-item-${index}`}
            alt={item?.reference?.altText || item?.reference?.imageDesktop?.title || 'Featured image'}
          />
        ))}
      </Flex>
      <Flex
        width="100%"
        height="100%"
        paddingAll={32}
        sm={{ paddingAll: 48 }}
        md={{ paddingAll: 64, width: '50%' }}
        flexDirection="column"
        css={`
          position: relative;

          .MuiTabScrollButton-horizontal {
            color: black;
          }
        `}
      >
        <Tabs
          value={active}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons={true}
          allowScrollButtonsMobile
          sx={{
            marginBottom: '72px',
            width: '100%',
            overflow: 'visible',

            '&& .MuiTabScrollButton-horizontal': {
              width: 0,
            },
            '&& .MuiButtonBase-root': {
              borderBottom: '2px solid #C1C2C3',
            },
            '&& .MuiTabScrollButton-root svg': {
              width: '1.5rem',
              height: '1.5rem',
            },
            '&& .MuiTabs-indicator': {
              backgroundColor: '#000',
            },
          }}
          css={`
            margin-top: -12px;

            && .MuiTabs-flexContainer {
              &::-webkit-scrollbar {
                display: none;
              }
            }

            .MuiTabScrollButton-root:first-child {
              max-width: 30px !important;
              padding-right: 0 !important;
              left: -15px;
            }

            .MuiTabScrollButton-root:last-child {
              max-width: 30px !important;
              padding-left: 0 !important;
              right: -15px;
            }

            @media (max-width: 575px) {
              margin-bottom: 45px !important;
            }
          `}
        >
          {switcherItem?.map(tabItem => (
            <Tab key={`${tabItem?.title} - tab`} label={tabItem?.title} sx={sx} />
          ))}
        </Tabs>
        {switcherItem?.map((item, index) => (
          <Flex
            className="w-full"
            display={active === index ? 'block' : 'none'}
            flexDirection="column"
            key={`switcher-item-${index}`}
            css={`
              h1,
              h2,
              h3,
              h4,
              h5,
              h6 {
                font-size: 24px;
              }

              margin-bottom: 48px;
            `}
          >
            <Flex textColor="gray-900">{richTextParser(item?.listHeading)}</Flex>
            <Flex
              flexDirection="column"
              gap="24px"
              width="100%"
              md={{ marginBottom: 80 }}
              className="mt-[30px] sm:mt-[48px]"
            >
              {item?.listItem?.map(subItem => (
                <Flex gap="18px" alignItems="flex-start" key={subItem?.id || 'List Item'}>
                  {subItem?.icon ? (
                    <Container className="w-[30px] !-mt-[5px] mr-2">
                      <Image
                        image={subItem.icon as ImageData}
                        alt={subItem.icon?.title || 'List Item Asset'}
                        height="100%"
                        width="100%"
                      />
                    </Container>
                  ) : (
                    <Container marginTop={4} className="w-[30px]">
                      <Image image={{ file: { url: checkIcon } }} alt="List Item Asset" height="100%" width="100%" />
                    </Container>
                  )}
                  <Flex flexDirection="column" gap="8px">
                    {subItem?.heading && (
                      <Text textStyle="textSm" textColor="gray-900">
                        {subItem.heading}
                      </Text>
                    )}
                    {subItem?.body && (
                      <Flex flexDirection="column" gap="16px" fontSize="textMd">
                        {richTextParser(subItem.body)}
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              ))}
            </Flex>
            {item?.callToAction && (
              <Flex
                gap="4px"
                css={`
                  position: absolute;
                  bottom: 50px;
                  width: 100%;

                  .MuiButtonBase-root {
                    width: calc(100% - 8rem) !important;
                    max-width: calc(100% - 8rem) !important;
                  }

                  @media (max-width: 767px) {
                    .MuiButtonBase-root {
                      width: calc(100% - 6rem) !important;
                      max-width: calc(100% - 6rem) !important;
                    }
                  }

                  @media (max-width: 575px) {
                    bottom: 2rem;

                    .MuiButtonBase-root {
                      width: calc(100% - 4rem) !important;
                      max-width: calc(100% - 4rem) !important;
                    }
                  }
                `}
              >
                <ComponentButton {...item?.callToAction} css={{ maxWidth: '100%' }} buttonColor="Sunrise 500" />
              </Flex>
            )}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export default TabbedSwitcherV2;
